import React, { FunctionComponent, useCallback } from "react";
import { Box, Typography, styled } from "@mui/material";
import { connect } from "react-redux";

import { AppState } from "@store";
import { getEncodedUrl } from "@lib/common";
import { ITEMTYPE, REDEEMTYPE } from "@ducks/listing/types";
import { CONTENT_IMAGETYPE, resizeImage } from "@lib/resizer";
import type { ILanguage, IResizerConfig } from "@ducks/config/types";

interface IVendorProps {
  logo: string;
  name: string;
  itemType: ITEMTYPE;
  redeemType: REDEEMTYPE;
  small?: boolean;
  language: ILanguage;
  resizer: IResizerConfig;
}

const Logo = styled("div")({
  position: "relative",
  display: "flex",
  alignItems: "center",
  overflow: "hidden",
  border: "1px solid #8b8b8b",
  borderRadius: "1.5rem",
  "& img": {
    width: "100%",
    maxWidth: "100%",
  },
});

const Content = styled("div")({
  width: "calc(100% - 3.5rem)",
  marginLeft: "0.5rem",
  "& h4": {
    margin: 0,
    overflow: "hidden",
    fontWeight: 700,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "& p": {
    margin: 0,
    fontSize: "0.875rem",
  },
});

const Vendor: FunctionComponent<IVendorProps> = (props) => {
  const { logo, name, itemType, redeemType, language, resizer } = props;

  const renderRedeemTypeText = useCallback(() => {
    switch (itemType.toLowerCase()) {
      case ITEMTYPE.promotion:
        return language.PROMOTION;
      case ITEMTYPE.reward:
        switch (redeemType) {
          case REDEEMTYPE.online:
            return language.REDEEM_ONLINE;
          case REDEEMTYPE.offline:
            return language.REDEEM_AT_PARTICIPATING_STORE;
          default:
            return "";
        }
      default:
        return "";
    }
  }, [itemType, redeemType, language]);

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Logo>
        <img
          loading="lazy"
          src={getEncodedUrl(resizeImage(logo || "", resizer, CONTENT_IMAGETYPE.vendor))}
          width={48}
          height={48}
          alt={name}
          title={name}
        />
      </Logo>
      <Content>
        <Typography sx={{ fontWeight: 700, fontSize: 1, lineHeight: "1.375rem", }}>
          {name}
        </Typography>
        <Typography
          paragraph
          sx={{ lineHeight: "1.25rem", fontSize: "0.875rem" }}
        >
          {renderRedeemTypeText()}
        </Typography>
      </Content>
    </Box>
  );
};

const mapStateToProps = (state: AppState) => ({
  language: state.config.language,
  resizer: state.config.resizer,
});

export default connect(mapStateToProps)(Vendor);
